// Carer-parity S2 — auth-context unification.
//
// BearerProvider exposes a bearer token sourced from a configurable
// storage backend. Coordinator subtree mounts a provider over
// sessionStorage:hcm_access_token; carer subtree mounts a provider
// over localStorage:carer_token. The provider registers a
// module-scoped glue object so lib/api.ts apiFetch can resolve the
// bearer at call time without consumer-side changes (170 apiFetch
// call sites unchanged).
//
// S2 sub-decision 3 = 3a: registration runs inline in the render
// body, not in useEffect. React 19 strict mode double-invokes the
// render — registration is idempotent (the new closure exposes the
// same behavior as the previous one), so double-invoke is a no-op.
// This avoids the mount-order race that an effect-based registration
// would create (useQuery firing before the effect runs).

import {
  createContext, useContext, useCallback,
  type ReactNode, type JSX,
} from "react";
import { registerBearerProvider } from "../lib/api.js";

export type StorageBackend = "sessionStorage" | "localStorage";

export interface BearerContextValue {
  getBearer: () => string | null;
  setBearer: (token: string | null) => void;
  /** Storage backend in use — consumed by useFetcher to pick the right fetcher. */
  backend: StorageBackend;
}

const BearerContext = createContext<BearerContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
  backend: StorageBackend;
  storageKey: string;
}

function pickStorage(backend: StorageBackend): Storage {
  return backend === "sessionStorage" ? sessionStorage : localStorage;
}

export function BearerProvider({ children, backend, storageKey }: ProviderProps): JSX.Element {
  const storage = pickStorage(backend);

  // getBearer reads through to storage on every call so a sibling
  // carerFetch silent-refresh that wrote to localStorage directly is
  // visible here without a re-render. setBearer writes through to
  // storage; no React state is held because no consumer subscribes
  // to bearer-change notifications (apiFetch reads at call time;
  // useWebSocket reads at connect time).
  const getBearer = useCallback((): string | null => storage.getItem(storageKey), [storage, storageKey]);
  const setBearer = useCallback((next: string | null): void => {
    if (next) storage.setItem(storageKey, next);
    else storage.removeItem(storageKey);
  }, [storage, storageKey]);

  // Inline synchronous registration. Idempotent: each render's closure
  // exposes the same behavior (same storage + same key). Strict-mode
  // double-invoke overwrites the slot with an equivalent closure.
  registerBearerProvider({ getBearer, setBearer });

  return (
    <BearerContext.Provider value={{ getBearer, setBearer, backend }}>
      {children}
    </BearerContext.Provider>
  );
}

export function useBearer(): BearerContextValue {
  const ctx = useContext(BearerContext);
  if (!ctx) throw new Error("useBearer must be used inside <BearerProvider>");
  return ctx;
}
